import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import { ShoppingBag, User, Menu as MenuIcon, X, LogOut, LayoutDashboard, Sun, Moon, Sparkles, GraduationCap } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useCart } from "@/context/CartContext";

const LOGO = "/assets/brand/logo-traiteur-fl-blanc.webp";

const links = [
  { to: "/", label: "Accueil" },
  { to: "/menu", label: "Mets préparés" },
  { to: "/buffets", label: "Studio Buffet" },
  { to: "/a-propos", label: "À propos" },
];

const THEMES = [
  { id: "light", label: "Clair", icon: Sun },
  { id: "dark", label: "Sombre", icon: Moon },
  { id: "premium", label: "Premium", icon: Sparkles },
];

export default function Navbar() {
  const { user, openAuth, logout, changeTheme } = useAuth();
  const { theme } = useTheme();
  const { count, openCart } = useCart();
  const nav = useNavigate();
  const loc = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [themeMenu, setThemeMenu] = useState(false);
  const CurrentThemeIcon = (THEMES.find((t) => t.id === theme) || THEMES[0]).icon;

  const handleScolaire = () => {
    window.open("https://ecodemo.dev/parent-login", "_blank", "noopener,noreferrer");
  };

  return (
    <header className="sticky top-0 z-50 glass border-b border-border">
      <nav className="mx-auto max-w-7xl px-4 sm:px-6 h-16 sm:h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
          <img src={LOGO} alt="Traiteur FL" className="logo-adaptive h-12 sm:h-16 w-auto" />
        </Link>

        <div className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label}`}
              className={`font-heading text-sm tracking-wide transition-colors ${
                loc.pathname === l.to ? "text-coral" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          <div className="relative">
            <button
              onClick={() => setThemeMenu((v) => !v)}
              data-testid="theme-toggle"
              className="p-2.5 rounded-full hover:bg-foreground/10 transition-colors"
              aria-label="Choisir le thème"
            >
              <CurrentThemeIcon className="h-5 w-5" />
            </button>
            {themeMenu && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setThemeMenu(false)} />
                <div className="absolute right-0 mt-2 w-44 rounded-xl glass border border-border p-1 shadow-xl z-50" data-testid="theme-menu">
                  {THEMES.map((t) => (
                    <button
                      key={t.id}
                      data-testid={`theme-option-${t.id}`}
                      onClick={() => { changeTheme(t.id); setThemeMenu(false); }}
                      className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-left transition-colors ${theme === t.id ? "bg-foreground/10 text-foreground font-medium" : "text-muted-foreground hover:bg-foreground/5"}`}
                    >
                      <t.icon className="h-4 w-4" /> {t.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
          <button
            onClick={handleScolaire}
            data-testid="nav-scolaire-button"
            className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-full border border-foreground/25 hover:bg-foreground/5 text-foreground font-heading text-sm font-medium transition-colors active:scale-95"
          >
            <GraduationCap className="h-4 w-4" /> Commande Scolaire
          </button>
          <button
            onClick={openCart}
            data-testid="nav-cart-button"
            className="relative p-2.5 rounded-full hover:bg-foreground/10 transition-colors"
            aria-label="Panier"
          >
            <ShoppingBag className="h-5 w-5" />
            {count > 0 && (
              <span
                data-testid="nav-cart-count"
                className="absolute -top-0.5 -right-0.5 bg-primary text-primary-foreground text-[11px] font-bold h-5 min-w-5 px-1 rounded-full flex items-center justify-center animate-bump"
              >
                {count}
              </span>
            )}
          </button>

          {user ? (
            <div className="hidden sm:flex items-center gap-2">
              {user.role === "admin" && (
                <button
                  onClick={() => nav("/admin")}
                  data-testid="nav-admin-button"
                  className="p-2.5 rounded-full hover:bg-foreground/10 transition-colors"
                  title="Administration"
                >
                  <LayoutDashboard className="h-5 w-5" />
                </button>
              )}
              <button
                onClick={() => nav("/compte")}
                data-testid="nav-account-button"
                className="flex items-center gap-2 px-3 py-2 rounded-full bg-foreground/5 hover:bg-foreground/10 transition-colors"
              >
                <User className="h-4 w-4" />
                <span className="text-sm max-w-[90px] truncate">{user.name}</span>
              </button>
              <button
                onClick={logout}
                data-testid="nav-logout-button"
                className="p-2.5 rounded-full hover:bg-foreground/10 transition-colors"
                title="Déconnexion"
              >
                <LogOut className="h-5 w-5" />
              </button>
            </div>
          ) : (
            <button
              onClick={openAuth}
              data-testid="nav-login-button"
              className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary hover:opacity-90 text-primary-foreground font-heading text-sm font-medium transition-colors active:scale-95"
            >
              <User className="h-4 w-4" /> Connexion
            </button>
          )}

          <button
            onClick={() => setMobileOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            className="md:hidden p-2.5 rounded-full hover:bg-foreground/10"
            aria-label="Menu"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <MenuIcon className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      {mobileOpen && (
        <div className="md:hidden glass border-t border-border px-4 py-4 flex flex-col gap-1 animate-fade-up">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              onClick={() => setMobileOpen(false)}
              data-testid={`nav-mobile-link-${l.label}`}
              className="py-3 px-2 rounded-lg hover:bg-foreground/5 font-heading text-foreground"
            >
              {l.label}
            </Link>
          ))}
          <div className="h-px bg-foreground/10 my-2" />
          <button
            onClick={() => { setMobileOpen(false); handleScolaire(); }}
            data-testid="nav-mobile-scolaire"
            className="flex items-center justify-center gap-2 py-3 px-2 rounded-lg border border-foreground/25 hover:bg-foreground/5 font-heading font-medium text-foreground"
          >
            <GraduationCap className="h-4 w-4" /> Commande Scolaire
          </button>
          {user ? (
            <>
              {user.role === "admin" && (
                <button onClick={() => { setMobileOpen(false); nav("/admin"); }} className="text-left py-3 px-2 rounded-lg hover:bg-foreground/5 font-heading">Administration</button>
              )}
              <button onClick={() => { setMobileOpen(false); nav("/compte"); }} className="text-left py-3 px-2 rounded-lg hover:bg-foreground/5 font-heading">Mon compte</button>
              <button onClick={() => { setMobileOpen(false); logout(); }} className="text-left py-3 px-2 rounded-lg hover:bg-foreground/5 font-heading text-coral">Déconnexion</button>
            </>
          ) : (
            <button
              onClick={() => { setMobileOpen(false); openAuth(); }}
              data-testid="nav-mobile-login"
              className="py-3 px-2 rounded-lg bg-primary text-primary-foreground font-heading font-medium text-center"
            >
              Connexion
            </button>
          )}
        </div>
      )}
    </header>
  );
}
